import type { EventCategoryDto, ExtractorConfigDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { useState } from 'react';
import { useUpdateExtractorConfig } from '../mutations';
import { WordListField } from './word-list-field';

interface Props {
  config: ExtractorConfigDto;
  categories: EventCategoryDto[];
}

const splitWords = (v: string) =>
  v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export function ExtractorConfigForm({ config, categories }: Props) {
  const update = useUpdateExtractorConfig();
  const [digestMaxLinks, setDigestMaxLinks] = useState(String(config.digestMaxLinks));
  const [digestMarkers, setDigestMarkers] = useState(config.digestMarkers.join(', '));
  const [recurringMarkers, setRecurringMarkers] = useState(config.recurringMarkers.join(', '));

  const activeCategories = categories.filter((c) => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  // Slug из конфига без активной категории (удалили/выключили) — не теряем слова молча.
  const orphanSlugs = Object.keys(config.categoryKeywords).filter(
    (slug) => !activeCategories.some((c) => c.slug === slug),
  );
  const [keywords, setKeywords] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const c of activeCategories) init[c.slug] = (config.categoryKeywords[c.slug] ?? []).join(', ');
    for (const slug of orphanSlugs) init[slug] = (config.categoryKeywords[slug] ?? []).join(', ');
    return init;
  });
  const setKeyword = (slug: string, value: string) => setKeywords((prev) => ({ ...prev, [slug]: value }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const categoryKeywords: Record<string, string[]> = {};
    for (const [slug, value] of Object.entries(keywords)) {
      const words = splitWords(value);
      if (words.length > 0) categoryKeywords[slug] = words;
    }
    update.mutate({
      digestMaxLinks: Number(digestMaxLinks),
      digestMarkers: splitWords(digestMarkers),
      recurringMarkers: splitWords(recurringMarkers),
      categoryKeywords,
    });
  };

  return (
    <form onSubmit={onSubmit} className="max-w-2xl space-y-6">
      <div className="space-y-2">
        <Label htmlFor="links">Порог ссылок для дайджеста (больше → дайджест)</Label>
        <Input
          id="links"
          type="number"
          min={0}
          value={digestMaxLinks}
          onChange={(e) => setDigestMaxLinks(e.target.value)}
        />
      </div>
      <WordListField
        id="dm"
        label="Слова-маркеры дайджеста (через запятую)"
        value={digestMarkers}
        onChange={setDigestMarkers}
      />
      <WordListField
        id="rm"
        label="Маркеры рекуррентности (через запятую)"
        value={recurringMarkers}
        onChange={setRecurringMarkers}
      />

      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Словарь категорий</h2>
          <p className="text-sm text-muted-foreground">
            Слова сравниваются как подстрока без учёта регистра — пишите основу: «экскурси» поймает
            «экскурсия/экскурсии». На пост вешается до 3 категорий.
          </p>
        </div>
        {activeCategories.map((c) => (
          <WordListField
            key={c.slug}
            id={`kw-${c.slug}`}
            label={`${c.name} (${c.slug})`}
            value={keywords[c.slug] ?? ''}
            onChange={(v) => setKeyword(c.slug, v)}
          />
        ))}
        {orphanSlugs.map((slug) => (
          <WordListField
            key={slug}
            id={`kw-${slug}`}
            label={`Неизвестная категория (${slug})`}
            value={keywords[slug] ?? ''}
            onChange={(v) => setKeyword(slug, v)}
            note="категория не активна — слова сохранятся, но не применяются"
          />
        ))}
      </section>

      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Сохраняем…' : 'Сохранить'}
      </Button>
      {update.isSuccess && <span className="ml-3 text-sm text-muted-foreground">Сохранено</span>}
    </form>
  );
}
